import {
  ClearAll,
  Close as CloseIcon,
  FilterAltOutlined,
  FilterList,
  ImportContacts,
  PersonAdd,
  RefreshOutlined,
  Search,
  ViewAgenda,
  ViewStream,
} from '@mui/icons-material';
import {
  alpha,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  InputBase,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { debounce } from '@mui/material/utils';
import { isEmpty } from 'lodash';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { deleteContact, getContacts, resetPagination, selectLimit } from 'src/features/account/contacts.slice';
import { fetchFilteredContacts, selectAllFilteredContacts, selectIsFilterActive, setFilter } from 'src/features/account/filteredContacts.slice';
import { setOpenDialog } from 'src/features/theme/theme.slice';
import ContactsService from 'src/services/contacts.services';
import { useAppDispatch, useAppSelector } from 'src/store';
import { Contact } from 'src/types/contacts';
import { getBranding } from 'src/utils/branding';
import BulkConversationCreateDialog from '../Channels/BulkConversationCreateDialog';
import BulkTagDialog from './BulkTagDialog';
import { ContactCreateDialog } from './ContactCreateDialog';
import { useContactDrawer } from './ContactDrawer';
import ContactsDataGrid from './ContactsDataGrid';
import ContactsFilterBar from './ContactsFilterBar';
import ContactsList from './ContactsList';
import ContactsPagination from './ContactsPagination';
import { ListsAndCards } from './ListAndCards';
import ContactsUploaderDialog from './uploader/ContactsUploaderDialog';
import { ContactFilters } from './types';

interface ContactsWrapperProps {
  contacts: Contact[];
}

const ContactsWrapper: React.FC = () => {
  const dispatch = useAppDispatch();
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>('grid');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const isFilterActive = useAppSelector(selectIsFilterActive);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [multiEditDialogOpen, setMultiEditDialogOpen] = useState(false);
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [filters, setFilters] = useState<ContactFilters>({});

  // Use ContactDrawer context
  const { openDrawer: openContactDrawer, closeDrawer: closeContactDrawer } = useContactDrawer();

  // State to control loading bar visibility
  const [showLoadingBar, setShowLoadingBar] = React.useState(false);

  // Add search term state
  const [searchTerm, setSearchTerm] = useState('');

  // Active filter count
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // State for filter drawer
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // State for hovering over filter controls
  const [isFilterControlsHovered, setIsFilterControlsHovered] = useState(false);

  // State for mobile search expansion
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const contacts = useAppSelector(selectAllFilteredContacts);
  const totalContacts = useAppSelector(state => state.contacts.totalContacts);
  const totalFilteredContacts = useAppSelector(state => state.filteredContacts.totalContacts);
  const contactsLoading = useAppSelector(
    (state) => state.filteredContacts.contactsLoading || state.contacts.contactsLoading
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const filterObject = useAppSelector((state) => state.filteredContacts.filterObject);
  const limit = useAppSelector(selectLimit);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCache, setPageCache] = useState<Record<number, Contact[]>>({});

  // Dynamic drawer width based on screen size
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const drawerWidth = isLargeScreen ? 520 : 420;

  // Use 'oppy_expressions_drunk' to match 404.tsx
  const oppyImage = getBranding('oppy_expressions_drunk');

  // Update URL when filters change
  const updateUrlWithFilters = useCallback((filters: Record<string, any>) => {
    const queryParams = new URLSearchParams(location.search);

    // Update or remove each filter in the URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        queryParams.set(key, value.toString());
      } else {
        queryParams.delete(key);
      }
    });

    // Make sure we keep the viewMode parameter if it exists
    const viewModeParam = queryParams.get('viewMode');
    if (!viewModeParam && viewMode) {
      queryParams.set('viewMode', viewMode);
    }

    // Update the URL without causing a navigation
    const newUrl = `${location.pathname}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    navigate(newUrl, { replace: true });
  }, [location.pathname, location.search, navigate, viewMode]);

  // Debounced search handler
  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        // Update URL with search term
        updateUrlWithFilters({ search: term });
        dispatch(setFilter({ query: term }));

        // Dispatch the action to fetch filtered contacts
        dispatch(fetchFilteredContacts({ query: term }));
      }, 1200),
    [dispatch, updateUrlWithFilters]
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    debouncedSearch(newSearchTerm);
    resetPagination();
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    debouncedSearch('');
  };

  // Check for contact_id and search in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const contactId = params.get('contact_id');
    const searchParam = params.get('search');

    // If contact_id is in URL, open the drawer
    if (contactId) {
      openContactDrawer(contactId);
    }

    // Set search term from URL if it exists
    if (searchParam) {
      setSearchTerm(searchParam);
      dispatch(setFilter({ query: searchParam }));
    }
  }, [location.search]);

  // Extract viewMode from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewModeParam = params.get('viewMode');
    if (viewModeParam === 'grid' || viewModeParam === 'kanban') {
      setViewMode(viewModeParam as 'grid' | 'kanban');
    }
  }, [location.search]);

  // initial load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');

    if (searchParam) {
      // If there's a search parameter, use it to fetch filtered contacts
      dispatch(fetchFilteredContacts({ query: searchParam }));
    } else {
      // Otherwise, fetch all contacts
      dispatch(getContacts({ limit }));
    }
  }, [dispatch, location.search]);

  // Update cache when contacts change
  useEffect(() => {
    // Only update cache if we have contacts and we're not in a loading state
    if (contacts.length > 0 && !contactsLoading) {
      // Add a ref to track if we already updated this page's cache to prevent infinite loops
      const currentContacts = pageCache[currentPage] || [];

      // Only update cache if the contacts have changed (different length or different IDs)
      if (currentContacts.length !== contacts.length ||
        !currentContacts.every((contact, index) => contact.id === contacts[index]?.id)) {
        setPageCache(prev => ({
          ...prev,
          [currentPage]: contacts
        }));
      }
    }
  }, [contacts, currentPage, contactsLoading, pageCache]);

  // Clear cache when filters change
  useEffect(() => {
    setPageCache({});
    setCurrentPage(1);
  }, [filterObject, limit]);

  // Function to clear cache and reset pagination
  const resetPaginationState = useCallback(() => {
    setPageCache({});
    setCurrentPage(1);
  }, []);

  // Effect: fetch contacts if any filters are active or when filters are cleared
  useEffect(() => {
    resetPaginationState();
    setShowLoadingBar(true);
    setSelectedContacts([]);

    if (isFilterActive) {
      dispatch(fetchFilteredContacts({
        ...filterObject,
        limit,
        cursor: null // Reset cursor when filters change
      } as any))
        .finally(() => {
          setShowLoadingBar(false);
          setIsInitialLoad(false);
        });
    } else {
      // When filters are cleared, fetch all contacts
      dispatch(getContacts({ limit }))
        .finally(() => {
          setShowLoadingBar(false);
          setIsInitialLoad(false);
        });
    }
  }, [dispatch, isFilterActive, filterObject, resetPaginationState]);

  // Effect: Set initial load to false after first contacts load
  useEffect(() => {
    if (!contactsLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [contactsLoading]);

  const handleViewModeChange = (newMode: 'grid' | 'kanban') => {
    setViewMode(newMode);
    updateUrlWithFilters({ viewMode: newMode });
  };

  const handleCreateContact = (newContact: any) => {
    dispatch(fetchFilteredContacts({}));
  };

  const handleOpenContactsUploaderDialog = () => {
    dispatch(setOpenDialog('ContactsUploaderDialog'));
  };

  // Calculate cursor for pagination
  const calculateCursor = useCallback((targetPage: number, currentPage: number) => {
    // If going to first page, no cursor needed
    if (targetPage === 1) return null;

    let cursor = null;

    // For forward navigation (next page)
    if (targetPage > currentPage) {
      // Get the last contact from the current page for forward navigation
      const lastContact = pageCache[currentPage]?.[pageCache[currentPage].length - 1];
      if (lastContact?.created_at) {
        cursor = lastContact.created_at;
      } else {
        // Fallback: try to find the last contact in the store if page cache is empty
        const lastContactInStore = contacts[contacts.length - 1]?.created_at;
        cursor = lastContactInStore || null;
      }
    }
    // For backward navigation (previous page)
    else if (targetPage < currentPage && targetPage > 1) {
      // Find a page we have in cache that is before the target page
      let foundPage = false;
      let referencePage = targetPage - 1;

      // Try to find a cached page to use as reference
      while (referencePage >= 1 && !foundPage) {
        if (pageCache[referencePage]?.length > 0) {
          const lastContact = pageCache[referencePage][pageCache[referencePage].length - 1];
          cursor = lastContact.created_at;
          foundPage = true;
        } else {
          referencePage--;
        }
      }

      // If we couldn't find a reference page, return a special value
      if (!foundPage && referencePage === 0) {
        return { needsReset: true, targetPage };
      }
    }

    return cursor;
  }, [pageCache, contacts]);

  const handlePageChange = useCallback((page: number) => {
    if (page === currentPage) return;

    // Store the current page before changing it
    const previousPage = currentPage;
    setCurrentPage(page);

    if (!isInitialLoad) {
      if (page === 1) {
        if (isFilterActive) {
          dispatch(fetchFilteredContacts({
            ...filterObject,
            limit,
            cursor: null
          } as any));
        } else {
          dispatch(resetPagination());
          dispatch(getContacts({ limit }));
        }
        return;
      }

      const cursorResult = calculateCursor(page, previousPage);

      if (cursorResult && typeof cursorResult === 'object' && 'needsReset' in cursorResult) {
        if (page === 2) {
          dispatch(resetPagination());
          dispatch(getContacts({ limit }));
          return;
        }

        setCurrentPage(1);
        dispatch(resetPagination());
        dispatch(getContacts({ limit }));
        return;
      }

      if (isFilterActive) {
        dispatch(fetchFilteredContacts({
          ...filterObject,
          limit,
          cursor: cursorResult
        } as any));
      } else {
        dispatch(getContacts({ limit, cursor: cursorResult }));
      }
    }
  }, [currentPage, isInitialLoad, isFilterActive, filterObject, dispatch, calculateCursor]);

  // Function to clear filters and fetch default contacts
  const handleClearFilters = () => {
    resetPaginationState();
    setSearchTerm(''); // Clear search term
    dispatch(setFilter({}));
    dispatch(fetchFilteredContacts({
      limit,
      cursor: null
    } as any));

    // Clear search from URL
    updateUrlWithFilters({ search: '' });
  };

  // Function to refresh filters
  const handleRefreshFilters = () => {
    if (isFilterActive || searchTerm) {
      setShowLoadingBar(true);

      // Create params from existing filter object
      const params = searchTerm ? { query: searchTerm, ...filterObject } : { ...filterObject };

      // Dispatch fetch with the current filter parameters
      dispatch(fetchFilteredContacts(params))
        .finally(() => {
          setShowLoadingBar(false);
        });
    }
  };

  const handleContactsSelection = (selectedIds: string[]) => {
    const selected = contacts.filter((contact) => selectedIds.includes(contact.id.toString()));
    setSelectedContacts(selected);
  };

  const handleBulkMessageClick = () => {
    setIsBulkDialogOpen(true);
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedContacts.length} selected contacts?`)) {
      Promise.all(selectedContacts.map(contact => dispatch(deleteContact(contact.id))))
        .then(() => {
          setSelectedContacts([]);
          dispatch(fetchFilteredContacts(filterObject));
        });
    }
  };

  // Function to handle bulk tag click
  const handleBulkTagClick = () => {
    setBulkTagDialogOpen(true);
  };

  // Function to handle bulk edit click
  const handleBulkEditClick = () => {
    setMultiEditDialogOpen(true);
  };

  // Get active filter count
  const getActiveFilterCount = (): number => {
    const count = Object.keys(filterObject || {}).length;
    return count;
  };

  // Get color for filter group
  const getFilterGroupColor = (group: string) => {
    switch (group) {
      case 'Details':
        return theme.palette.primary.main;
      case 'Company':
        return theme.palette.success.main;
      case 'Activity':
        return theme.palette.info.main;
      case 'Advanced':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Define styles
  const styles = {
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      mr: 2,
    },
    noContactsContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      color: darkMode ? 'grey.300' : 'text.primary',
      borderRadius: '8px',
      border: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`,
      p: 4,
    },
    oppyImage: {
      width: '200px',
      mb: 2,
      filter: darkMode ? 'brightness(0.8)' : 'none',
    },
    clearButton: {
      mt: 2,
      color: darkMode ? 'grey.300' : 'primary.main',
      '&:hover': {
        bgcolor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
      },
    },
    wrapper: {
      color: darkMode ? 'grey.100' : 'text.primary',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      // Only adjust padding on desktop when filter drawer is open
      pr: (isMobile || isTablet) ? 0 : (isFilterDrawerOpen ? `${drawerWidth}px` : 0),
    },
    contentContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      height: '100%',
      p: 2,
    },
    gridContainer: {
      flex: 1,
      overflow: 'auto',
      minHeight: 0, // Important to make the flex layout work properly
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
        display: 'block',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: darkMode
          ? alpha(theme.palette.background.paper, 0.3)
          : alpha(theme.palette.background.default, 0.3),
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: darkMode
          ? alpha(theme.palette.primary.main, 0.2)
          : alpha(theme.palette.primary.main, 0.2),
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: darkMode
            ? alpha(theme.palette.primary.main, 0.3)
            : alpha(theme.palette.primary.main, 0.3),
        },
      },
      scrollbarWidth: 'thin',
    },
    contentWrapper: {
      position: 'relative',
      borderRadius: '8px',
      border: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`,
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0, // Important for flex child to respect parent height
    },
    paginationContainer: {
      mt: 'auto',
      borderTop: `1px solid ${darkMode ? alpha(theme.palette.divider, 0.1) : theme.palette.divider}`,
      backgroundColor: darkMode ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
      borderRadius: '0 0 8px 8px',
    },
    filterSummary: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1,
      p: 1.5,
      borderBottom: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`,
      bgcolor: darkMode ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.primary.main, 0.02),
    },
    bulkActions: {
      display: 'flex',
      gap: 2,
      mt: 2,
      '& .MuiButton-root': {
        borderRadius: '8px',
        fontWeight: 500,
        textTransform: 'none',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
      color: darkMode ? 'grey.300' : 'text.primary',
    },
    iconButton: {
      borderRadius: '8px',
      p: 1,
      mx: 0.5,
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-1px)',
      },
    },
    createButton: {
      borderRadius: '8px',
      textTransform: 'none',
      fontWeight: 500,
      px: 2,
      boxShadow: 'none',
      backgroundColor: darkMode ? alpha(theme.palette.primary.main, 0.8) : theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        transform: 'translateY(-1px)',
      },
    },
    filterBadge: {
      '& .MuiBadge-badge': {
        bgcolor: isFilterActive ? theme.palette.primary.main : 'grey.500',
        color: 'white',
      },
    },
    filterRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 1,
      mt: 1,
      mb: 1,
      px: 2,
    },
    filterChip: {
      height: 28,
      borderRadius: 1,
      bgcolor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      fontSize: '0.8rem',
      '& .MuiChip-deleteIcon': {
        color: theme.palette.primary.main,
        '&:hover': {
          color: theme.palette.error.main,
        },
      },
    },
    filterControls: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      ml: 'auto',
    },
  };

  // Lazy load MultiEditDialog
  const MultiEditDialog = React.useMemo(
    () => lazy(() => import('./MultiEditDialog')),
    []
  );

  const handleSyncWithFub = () => {
    ContactsService.syncWithFub()
      .then(response => {
      })
      .catch(error => {
        console.error('Error syncing with FUB:', error);
      });
  };

  // Render active filter summary
  const FilterSummary = () => {
    if (!isFilterActive && !searchTerm) {
      return null;
    }

    // Count filters by type to display a summary
    const filterCounts: Record<string, number> = {};
    Object.keys(filterObject).forEach(key => {
      const filterType = key.includes('_') ? key.split('_')[0] : key;
      filterCounts[filterType] = (filterCounts[filterType] || 0) + 1;
    });

    return (
      <Fade in={isFilterActive || !!searchTerm}>
        <Box sx={styles.filterSummary}>
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FilterAltOutlined fontSize="small" />
                <Typography variant="body2" fontWeight={500}>
                  {getActiveFilterCount()} Active {getActiveFilterCount() === 1 ? 'Filter' : 'Filters'}
                </Typography>
              </Box>
            }
            sx={{
              bgcolor: darkMode
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              height: 28,
              fontWeight: 500,
              borderRadius: '6px',
            }}
          />

          {searchTerm && (
            <Chip
              label={`Search: "${searchTerm}"`}
              size="small"
              onDelete={handleClearSearch}
              sx={{
                bgcolor: darkMode
                  ? alpha(theme.palette.info.main, 0.15)
                  : alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
                height: 28,
                fontWeight: 500,
                borderRadius: '6px',
              }}
            />
          )}

          {/* Display filter groups summary */}
          {Object.entries(filterCounts).length > 0 && Object.entries(filterCounts).map(([group, count]) => (
            <Chip
              key={group}
              size="small"
              label={`${group}: ${count}`}
              sx={{
                bgcolor: darkMode
                  ? alpha(getFilterGroupColor(group), 0.15)
                  : alpha(getFilterGroupColor(group), 0.1),
                color: getFilterGroupColor(group),
                height: 28,
                fontWeight: 500,
                borderRadius: '6px',
              }}
            />
          ))}

          {contacts.length > 0 && totalFilteredContacts > 0 && (
            <Chip
              label={`${totalFilteredContacts} contact${totalFilteredContacts !== 1 ? 's' : ''} found`}
              size="small"
              sx={{
                bgcolor: darkMode
                  ? alpha(theme.palette.success.main, 0.15)
                  : alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                height: 28,
                fontWeight: 500,
                borderRadius: '6px',
              }}
            />
          )}

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Clear all filters" arrow>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <ClearAll fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>
    );
  };

  // Function to handle opening contact dialog
  const handleOpenContactDrawer = (contactId: string) => {
    openContactDrawer(contactId);

    // Update URL with contact_id
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('contact_id', contactId);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  // Function to handle closing contact drawer
  const handleCloseContactDrawer = () => {
    closeContactDrawer();

    // Remove contact_id from URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('contact_id');

    // Preserve other parameters like viewMode
    const newUrl = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    navigate(newUrl, { replace: true });
  };

  const renderViewComponent = () => {
    // Switch based on viewMode to render the appropriate view component
    switch (viewMode) {
      case 'grid':
        return (
          <ContactsDataGrid
            onSelectionChange={handleContactsSelection}
            selectedContactIds={selectedContacts.map(contact => contact.id.toString())}
            currentPage={currentPage}
            onOpenContactDialog={handleOpenContactDrawer}
          />
        );
      case 'kanban':
        return (
          <ListsAndCards
            boardMode="contacts"
          />
        );
      default:
        return (
          <ContactsList
            onSelectionChange={handleContactsSelection}
            selectedContactIds={selectedContacts.map(contact => contact.id.toString())}
            onOpenContactDialog={handleOpenContactDrawer}
          />
        );
    }
  };

  return (
    <Box sx={styles.wrapper}>
      {/* Use ContactsFilterBar with header functionality */}
      <ContactsFilterBar
        isDrawerOpen={isFilterDrawerOpen}
        setDrawerOpen={setIsFilterDrawerOpen}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        onCreateContact={() => setIsCreateDialogOpen(true)}
        onImportContacts={handleOpenContactsUploaderDialog}
        isLoading={contactsLoading}
        darkMode={darkMode}
      />

      <Box sx={styles.contentContainer}>
        <Box sx={styles.contentWrapper}>
          {showLoadingBar && <LinearProgress sx={{ mb: 0, bgcolor: darkMode ? 'grey.800' : undefined }} />}

          {selectedContacts && selectedContacts.length > 0 && (
            <Box sx={styles.bulkActions}>
              <Button
                variant="contained"
                onClick={handleBulkMessageClick}
                sx={{
                  bgcolor: darkMode ? alpha(theme.palette.primary.main, 0.8) : theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                Send Bulk Message
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBulkTagClick}
                sx={{
                  bgcolor: darkMode ? alpha(theme.palette.primary.main, 0.8) : theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                Tag Selected ({selectedContacts.length})
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleBulkDelete}
                sx={{
                  bgcolor: darkMode ? alpha(theme.palette.error.main, 0.8) : theme.palette.error.main,
                  '&:hover': {
                    bgcolor: theme.palette.error.dark,
                  },
                }}
              >
                Delete Selected ({selectedContacts.length})
              </Button>
            </Box>
          )}

          {contactsLoading && contacts.length === 0 ? (
            <Box sx={styles.loadingContainer}>
              <CircularProgress sx={{ color: darkMode ? 'grey.300' : 'primary.main' }} />
            </Box>
          ) : !contactsLoading && contacts.length === 0 ? (
            <Box sx={styles.noContactsContainer}>
              <img src={oppyImage} alt="No contacts" style={{ width: '200px', marginBottom: '16px' }} />
              <Typography variant="h6" sx={{ color: darkMode ? 'grey.300' : 'text.primary', mb: 2 }}>
                {isEmpty(filterObject) && !searchTerm ? "No contacts here yet." : "Couldn't find any contacts."}
              </Typography>
              <Typography variant="body1" sx={{ color: darkMode ? 'grey.400' : 'text.secondary', mb: 2 }}>
                {isEmpty(filterObject) && !searchTerm
                  ? "Add your first contact to get started."
                  : "Try adjusting your search or clearing the filters."}
              </Typography>
              {isEmpty(filterObject) && !searchTerm ? (
                <Button
                  variant="contained"
                  onClick={() => setIsCreateDialogOpen(true)}
                  sx={{
                    ...styles.createButton,
                    px: 3,
                    py: 1,
                  }}
                >
                  Add Contact
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClearFilters}
                  startIcon={<FilterList />}
                  sx={{
                    ...styles.clearButton,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, backgroundColor: theme.palette.background.paper }}>
                {renderViewComponent()}
              </Box>
              <Box sx={styles.paginationContainer}>
                <ContactsPagination
                  totalItems={isFilterActive || searchTerm ? totalFilteredContacts : totalContacts}
                  onPageChange={handlePageChange}
                  pageCache={pageCache}
                  setPageCache={setPageCache}
                  loading={contactsLoading}
                  currentPageProp={currentPage}
                  viewMode={viewMode}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      <ContactCreateDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        handleContactUpdate={handleCreateContact}
      />

      <ContactsUploaderDialog />

      {multiEditDialogOpen && (
        <Suspense fallback={<div>Loading...</div>}>
          <MultiEditDialog
            open={multiEditDialogOpen}
            onClose={() => setMultiEditDialogOpen(false)}
            selectedContactIds={selectedContacts.map(contact => contact.id.toString())}
          />
        </Suspense>
      )}

      <BulkConversationCreateDialog
        open={isBulkDialogOpen}
        handleClose={() => setIsBulkDialogOpen(false)}
        selectedContacts={selectedContacts}
      />

      <BulkTagDialog
        open={bulkTagDialogOpen}
        onClose={() => setBulkTagDialogOpen(false)}
        selectedContactIds={selectedContacts.map(contact => contact.id.toString())}
      />

    </Box>
  );
};

export default ContactsWrapper;
